'use client';

import * as React from 'react';
import { SubGenreChip } from '@/components/atoms/SubGenreChip';
import { cn } from '@/components/atoms/primitives/utils';
// WEB-801: these live in a directive-free module because `CategoryHubTemplate`
// (a Server Component) CALLS them during the server render. Defining or
// re-exporting them from this `'use client'` file makes them client references
// on the server, which throws. Import from `./ids` — never re-export here.
import { subGenrePanelId, subGenreTabId } from './ids';

export interface SubGenreChipsItem {
  slug: string;
  label: string;
  count: number;
}

export interface SubGenreChipsProps {
  items: SubGenreChipsItem[];
  /** Currently selected slug. `null` (or omitted) means "All". */
  selectedSlug?: string | null;
  onChange?: (slug: string | null) => void;
  /** Accessible name for the tablist (e.g. "Filter by sub-genre"). */
  label?: string;
  /**
   * Canonical event total for the "All" chip (WEB-314). Must be the same
   * source of truth used by the H1, liveregion, and footer CTA — typically
   * `meta.eventCount` from `fetchCategoryMeta()`. When omitted, falls back
   * to summing chip counts; that fallback is wrong on the category hub
   * because the sub-genre aggregation `COUNT(*)`s each (event, genre) pair
   * (an event with two genres is counted twice) and ignores the meta
   * endpoint's upcoming-window cap. Pass the prop explicitly anywhere
   * count parity across surfaces matters.
   */
  totalCount?: number;
  className?: string;
}

type Entry = { slug: string | null; label: string; count: number };

export function SubGenreChips({
  items,
  selectedSlug = null,
  onChange,
  label = 'Filter by sub-genre',
  totalCount,
  className,
}: SubGenreChipsProps): React.ReactElement {
  const entries: Entry[] = React.useMemo(() => {
    // WEB-314: prefer the canonical `totalCount` (rooted at
    // `meta.eventCount`) so the "All" chip matches the H1 sub-header,
    // liveregion, and footer CTA. Fall back to a chip-sum only when the
    // caller has no canonical total — that path is intentionally last so
    // it doesn't silently win on the category hub.
    const allCount =
      typeof totalCount === 'number'
        ? totalCount
        : items.reduce((sum, item) => sum + item.count, 0);
    return [{ slug: null, label: 'All', count: allCount }, ...items];
  }, [items, totalCount]);

  const chipRefs = React.useRef<Array<HTMLButtonElement | null>>([]);
  if (chipRefs.current.length !== entries.length) {
    chipRefs.current = new Array(entries.length).fill(null);
  }

  const currentIndex = entries.findIndex((e) => e.slug === selectedSlug);
  const activeIndex = currentIndex === -1 ? 0 : currentIndex;

  function selectByIndex(index: number): void {
    const wrapped = ((index % entries.length) + entries.length) % entries.length;
    onChange?.(entries[wrapped]?.slug ?? null);
    // Move focus to follow selection (WAI-ARIA tablist automatic activation).
    chipRefs.current[wrapped]?.focus();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        selectByIndex(activeIndex + 1);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        selectByIndex(activeIndex - 1);
        break;
      case 'Home':
        event.preventDefault();
        selectByIndex(0);
        break;
      case 'End':
        event.preventDefault();
        selectByIndex(entries.length - 1);
        break;
      default:
        break;
    }
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      aria-orientation="horizontal"
      // Mirrors the `.cat-genres` row: wrapping flex with a small gutter.
      className={cn('flex flex-wrap items-center gap-1.5', className)}
      onKeyDown={handleKeyDown}
    >
      {entries.map((entry, index) => {
        const isActive = index === activeIndex;
        return (
          <SubGenreChip
            key={entry.slug ?? '__all__'}
            id={subGenreTabId(entry.slug)}
            ariaControls={subGenrePanelId(entry.slug)}
            label={entry.label}
            count={entry.count}
            isActive={isActive}
            tabIndex={isActive ? 0 : -1}
            buttonRef={(el) => {
              chipRefs.current[index] = el;
            }}
            onSelect={() => onChange?.(entry.slug)}
          />
        );
      })}
    </div>
  );
}
